import React, { useState } from 'react';
import { Layout, Palette, Upload, Trash2, Plus, Type, Check, ChevronRight, ChevronLeft } from 'lucide-react';
import type { FlowQuestion, FlowAnswers, Ratio, Style, Tone } from '../../../types';

interface FlowPageProps {
  questions: FlowQuestion[];
  answers: FlowAnswers;
  onAnswerChange: (id: string, value: string | boolean | File[]) => void;
  ratios: Ratio[];
  styles: Style[];
  tones: Tone[];
  onNext: () => void;
  onPrev: () => void;
  isFirstPage: boolean;
  nextLabel?: string;
}

// ── 特殊 ID 渲染元件 ──────────────────────────────────────────────────────────

function RequiredBadge({ required }: { required?: boolean }) {
  if (required === false) {
    return <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700">選填</span>;
  }
  return <span className="ml-1 text-red-500 font-bold">*</span>;
}

function RatioSection({ ratios, answers, onAnswerChange, required, failed }: {
  ratios: Ratio[];
  answers: FlowAnswers;
  onAnswerChange: (id: string, value: string | boolean | File[]) => void;
  required?: boolean;
  failed?: boolean;
}) {
  const selected = answers['imageAspectRatio'] as string;
  return (
    <section className={`p-4 md:p-6 rounded-3xl space-y-4 md:space-y-6 border transition-colors ${failed ? 'bg-red-50/60 dark:bg-red-900/10 border-red-400 dark:border-red-600 shake' : 'bg-white/80 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800'}`}>
      <div className="flex items-center gap-2 text-blue-500 dark:text-blue-400 mb-2">
        <Layout size={20} />
        <h3 className="font-semibold tracking-wide text-slate-800 dark:text-slate-200 flex items-center">選擇輸出規格<RequiredBadge required={required} /></h3>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {ratios.map(r => (
          <button key={r.id} onClick={() => onAnswerChange('imageAspectRatio', r.id)}
            className={`p-4 rounded-2xl border transition-all flex flex-col items-center gap-3 ${selected === r.id
              ? 'bg-blue-600/10 border-blue-500 text-slate-900 dark:text-white shadow-[0_0_15px_rgba(59,130,246,0.2)]'
              : 'bg-slate-100 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
            }`}
          >
            <div className={`w-12 border-2 rounded ${r.class} ${selected === r.id ? 'border-blue-500 dark:border-white' : 'border-slate-400 dark:border-slate-500'}`} />
            <span className="text-sm font-medium">{r.name}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function StyleSection({ styles, answers, onAnswerChange, required, failed }: {
  styles: Style[];
  answers: FlowAnswers;
  onAnswerChange: (id: string, value: string | boolean | File[]) => void;
  required?: boolean;
  failed?: boolean;
}) {
  const selected = answers['imageStyle'] as string;
  return (
    <section className={`p-4 md:p-6 rounded-3xl space-y-4 md:space-y-6 border transition-colors ${failed ? 'bg-red-50/60 dark:bg-red-900/10 border-red-400 dark:border-red-600 shake' : 'bg-white/80 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800'}`}>
      <div className="flex items-center gap-2 text-emerald-500 dark:text-emerald-400 mb-2">
        <Palette size={20} />
        <h3 className="font-semibold tracking-wide text-slate-800 dark:text-slate-200 flex items-center">視覺設計風格<RequiredBadge required={required} /></h3>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {styles.map(s => (
          <button key={s.id} onClick={() => onAnswerChange('imageStyle', s.id)}
            className={`relative overflow-hidden p-4 h-24 rounded-2xl border transition-all ${selected === s.id
              ? 'border-blue-500 ring-2 ring-blue-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900'
              : 'border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
            }`}
          >
            <div className={`absolute inset-0 opacity-60 ${s.bg}`} />
            <div className="relative z-10 h-full flex items-end">
              <span className="text-sm font-bold text-white drop-shadow-md">{s.name}</span>
              {selected === s.id && <Check className="ml-auto text-white" size={16} />}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function UploadSection({ answers, onAnswerChange, required, failed, minUpload, maxUpload }: {
  answers: FlowAnswers;
  onAnswerChange: (id: string, value: string | boolean | File[]) => void;
  required?: boolean;
  failed?: boolean;
  minUpload?: number;
  maxUpload?: number;
}) {
  const files = (answers['imageUpload'] as File[]) || [];
  const previews = files.map(f => URL.createObjectURL(f));
  const maxU = maxUpload ?? 5;
  const minU = minUpload ?? (required !== false ? 1 : 0);
  const atMax = files.length >= maxU;

  const handleAssetUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []).slice(0, maxU - files.length);
    onAnswerChange('imageUpload', [...files, ...newFiles]);
  };

  const removeAsset = (index: number) => {
    onAnswerChange('imageUpload', files.filter((_, i) => i !== index));
  };

  return (
    <section className={`p-4 md:p-6 rounded-3xl space-y-4 md:space-y-6 border transition-colors ${failed ? 'bg-red-50/60 dark:bg-red-900/10 border-red-400 dark:border-red-600 shake' : 'bg-white/80 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800'}`}>
      <div className="flex items-center gap-2 text-purple-500 dark:text-purple-400">
        <Upload size={20} />
        <h3 className="font-semibold tracking-wide text-slate-800 dark:text-slate-200 flex items-center">上傳活動素材照片<RequiredBadge required={required} /></h3>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {previews.map((preview, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 group">
            <img src={preview} className="w-full h-full object-cover" alt="Upload" />
            <button onClick={() => removeAsset(i)}
              className="absolute top-1 right-1 p-1 bg-red-500/80 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {!atMax && (
          <label className="aspect-square border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 hover:border-blue-500 hover:text-blue-500 transition-all cursor-pointer">
            <Plus size={24} />
            <span className="text-[10px] mt-1">點擊新增</span>
            <input type="file" multiple accept="image/*" onChange={handleAssetUpload} className="hidden" />
          </label>
        )}
      </div>
      {(minU > 0 || maxUpload) && (
        <p className={`text-xs ${files.length < minU ? 'text-amber-500' : 'text-slate-400 dark:text-slate-500'}`}>
          已上傳 {files.length} / {maxU} 張{minU > 0 ? `（最少 ${minU} 張）` : ''}
        </p>
      )}
      {/* <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl">
        <input
          type="checkbox"
          id="usePersonalPhoto"
          checked={usePersonal || false}
          onChange={(e) => onAnswerChange('usePersonalPhoto', e.target.checked)}
          className="w-5 h-5 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 bg-white dark:bg-slate-800"
        />
        <label htmlFor="usePersonalPhoto" className="text-slate-600 dark:text-slate-300 font-medium cursor-pointer select-none flex items-center gap-2">
          <span>套用個人形象照</span>
          <span className="text-xs text-slate-400 dark:text-slate-500">(將參考您的個人形象照進行生成)</span>
        </label>
      </div> */}
    </section>
  );
}

function ToneContentSection({ tones, answers, onAnswerChange, required, failed }: {
  tones: Tone[];
  answers: FlowAnswers;
  onAnswerChange: (id: string, value: string | boolean | File[]) => void;
  required?: boolean;
  failed?: boolean;
}) {
  const selectedTone = answers['textToneInImage'] as string;
//   const content = (answers['contentInImage'] as string) || '';

  return (
    <section className={`p-4 md:p-6 rounded-3xl space-y-4 md:space-y-6 border transition-colors ${failed ? 'bg-red-50/60 dark:bg-red-900/10 border-red-400 dark:border-red-600 shake' : 'bg-white/80 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800'}`}>
      <div className="flex items-center gap-2 text-amber-500 dark:text-amber-400">
        <Type size={20} />
        <h3 className="font-semibold tracking-wide text-slate-800 dark:text-slate-200 flex items-center">語氣選擇<RequiredBadge required={required} /></h3>
      </div>
      <div className="space-y-4">
        <div className="flex gap-2 flex-wrap">
          {tones.map(t => (
            <button key={t.id} onClick={() => onAnswerChange('textToneInImage', t.id)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${selectedTone === t.id
                ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>
        {/* <textarea
          className="w-full h-32 bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
          placeholder="例如：本週日早上有免費健康講座，地點在活動中心，請大家準時參加..."
          value={content}
          onChange={(e) => onAnswerChange('contentInImage', e.target.value)}
        /> */}
      </div>
    </section>
  );
}

// ── 主元件 ─────────────────────────────────────────────────────────────────────

const FlowPage: React.FC<FlowPageProps> = ({
  questions, answers, onAnswerChange,
  ratios, styles, tones,
  onNext, onPrev, isFirstPage, nextLabel,
}) => {
    
  const ratioQ = questions.find(q => q.id === 'imageAspectRatio');
  const styleQ = questions.find(q => q.id === 'imageStyle');
  const uploadQ = questions.find(q => q.id === 'imageUpload');
  const toneQ = questions.find(q => q.id === 'textToneInImage');

  // IDs handled by special sections
  const specialIds = new Set<string>();
  if (ratioQ) specialIds.add('imageAspectRatio');
  if (styleQ) specialIds.add('imageStyle');
  if (uploadQ) { specialIds.add('imageUpload') }
  if (toneQ) { specialIds.add('textToneInImage') }

  // ── Validation ────────────────────────────────────────────────────────────
  const getFailedIds = (): string[] =>
    questions
      .filter(q => q.required !== false)
      .filter(q => {
        const val = answers[q.id];
        if (q.type === 'text') return !(typeof val === 'string' && val.trim().length > 0);
        if (q.type === 'select') return !(typeof val === 'string' && val.length > 0);
        if (q.type === 'image') {
          const count = Array.isArray(val) ? val.length : 0;
          const min = q.minUpload ?? 1;
          return count < min;
        }
        return false;
      })
      .map(q => q.id);

  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());
  const [shakeKey, setShakeKey] = useState(0);

  // ── Special section map ────────────────────────────────────────────────────
  const specialComponentMap: Record<string, React.ReactNode> = {};
  if (ratioQ) specialComponentMap['imageAspectRatio'] = <RatioSection key={`ratio-${failedIds.has('imageAspectRatio') ? shakeKey : 0}`} ratios={ratios} answers={answers} onAnswerChange={onAnswerChange} required={ratioQ.required} failed={failedIds.has('imageAspectRatio')} />;
  if (styleQ) specialComponentMap['imageStyle'] = <StyleSection key={`style-${failedIds.has('imageStyle') ? shakeKey : 0}`} styles={styles} answers={answers} onAnswerChange={onAnswerChange} required={styleQ.required} failed={failedIds.has('imageStyle')} />;
  if (uploadQ) specialComponentMap['imageUpload'] = <UploadSection key={`upload-${failedIds.has('imageUpload') ? shakeKey : 0}`} answers={answers} onAnswerChange={onAnswerChange} required={uploadQ.required} failed={failedIds.has('imageUpload')} minUpload={uploadQ.minUpload} maxUpload={uploadQ.maxUpload} />;
  if (toneQ) specialComponentMap['textToneInImage'] = <ToneContentSection key={`tone-${failedIds.has('textToneInImage') ? shakeKey : 0}`} tones={tones} answers={answers} onAnswerChange={onAnswerChange} required={toneQ.required} failed={failedIds.has('textToneInImage')} />;

  const specialSections = questions
    .filter(q => q.id in specialComponentMap)
    .map(q => specialComponentMap[q.id]);

  const remainingQuestions = questions.filter(q => !specialIds.has(q.id));

  // Track which template is selected per text question id
  const [activeTemplates, setActiveTemplates] = useState<Record<string, string>>({});

  const handleChange = (id: string, value: string | boolean | File[]) => {
    onAnswerChange(id, value);
    // Clear failed highlight as soon as user provides a valid answer
    setFailedIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleNext = () => {
    const failed = getFailedIds();
    if (failed.length > 0) {
      setFailedIds(new Set(failed));
      setShakeKey(k => k + 1);
      return;
    }
    setFailedIds(new Set());
    onNext();
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15%       { transform: translateX(-7px); }
          30%       { transform: translateX(7px); }
          45%       { transform: translateX(-4px); }
          60%       { transform: translateX(4px); }
          75%       { transform: translateX(-2px); }
          90%       { transform: translateX(2px); }
        }
        .shake { animation: shake 0.45s ease-in-out; }
      `}</style>
      {/* Special sections: 2-column grid when 2+, otherwise full width */}
      {specialSections.length > 0 && (
        <div className={`grid gap-4 md:gap-8 ${specialSections.length >= 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
          {specialSections}
        </div>
      )}

      {/* Remaining questions not handled by special sections */}
      {remainingQuestions.map(q => {
        const isFailed = failedIds.has(q.id);
        return (
        <div key={isFailed ? `${q.id}-${shakeKey}` : q.id} className={`rounded-3xl border transition-colors ${isFailed ? 'bg-red-50/60 dark:bg-red-900/10 border-red-400 dark:border-red-600 shake' : 'bg-white/80 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800'} ${q.type === 'boolean' ? 'px-4 md:px-6 py-3 md:py-4 inline-flex items-center gap-4' : 'p-4 md:p-6'}`}>
          {q.type === 'boolean' ? (
            <span className="font-semibold text-slate-800 dark:text-slate-200 flex items-center">{q.title}<RequiredBadge required={q.required} /></span>
          ) : (
            <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center">{q.title}<RequiredBadge required={q.required} /></h3>
          )}
          {q.type === 'text' && (
            <div className="space-y-3">
              <textarea
                className="w-full h-32 bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 placeholder:whitespace-pre-line focus:ring-2 focus:ring-blue-500 outline-none"
                value={(answers[q.id] as string) || ''}
                placeholder={q.placeholder}
                onChange={e => {
                  handleChange(q.id, e.target.value);
                  // Deactivate chip if user edits manually
                  if (activeTemplates[q.id] && e.target.value !== activeTemplates[q.id]) {
                    setActiveTemplates(prev => { const n = { ...prev }; delete n[q.id]; return n; });
                  }
                }}
              />
              {(q.templates ?? []).length > 0 && (
                <div>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-1.5">快速套用範本</p>
                  <div className="flex gap-2 flex-wrap">
                    {(q.templates ?? []).map((tpl, ti) => (
                      <button
                        key={ti}
                        type="button"
                        onClick={() => {
                          handleChange(q.id, tpl);
                          setActiveTemplates(prev => ({ ...prev, [q.id]: tpl }));
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all max-w-[200px] truncate ${
                          activeTemplates[q.id] === tpl
                            ? 'bg-blue-500 text-white border border-blue-500'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500'
                        }`}
                        title={tpl}
                      >
                        {tpl.includes('\n') ? `${tpl.split('\n')[0]}…` : tpl}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {q.type === 'image' && (() => {
            const files = (answers[q.id] as File[]) || [];
            const previews = files.map(f => URL.createObjectURL(f));
            const maxU = q.maxUpload ?? 5;
            const minU = q.minUpload ?? (q.required !== false ? 1 : 0);
            const atMax = files.length >= maxU;
            return (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  {previews.map((preview, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 group">
                      <img src={preview} className="w-full h-full object-cover" alt="Upload" />
                      <button
                        onClick={() => handleChange(q.id, files.filter((_, fi) => fi !== i))}
                        className="absolute top-1 right-1 p-1 bg-red-500/80 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                  {!atMax && (
                    <label className="aspect-square border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 hover:border-blue-500 hover:text-blue-500 transition-all cursor-pointer">
                      <Plus size={24} />
                      <span className="text-[10px] mt-1">點擊新增</span>
                      <input type="file" multiple accept="image/*" onChange={e => {
                        const newFiles = Array.from(e.target.files || []).slice(0, maxU - files.length);
                        handleChange(q.id, [...files, ...newFiles]);
                      }} className="hidden" />
                    </label>
                  )}
                </div>
                <p className={`text-xs ${files.length < minU ? 'text-amber-500' : 'text-slate-400 dark:text-slate-500'}`}>
                  已上傳 {files.length} / {maxU} 張{minU > 0 ? `（最少 ${minU} 張）` : ''}
                </p>
              </div>
            );
          })()}
          {q.type === 'boolean' && (
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-sm font-medium transition-colors ${!(answers[q.id] as boolean) ? 'text-slate-500 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'}`}>否</span>
              <button
                type="button"
                role="switch"
                aria-checked={(answers[q.id] as boolean) || false}
                onClick={() => handleChange(q.id, !((answers[q.id] as boolean) || false))}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  (answers[q.id] as boolean) ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-md transition-transform ${
                  (answers[q.id] as boolean) ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
              <span className={`text-sm font-medium transition-colors ${(answers[q.id] as boolean) ? 'text-blue-500' : 'text-slate-400 dark:text-slate-500'}`}>是</span>
            </div>
          )}
          {q.type === 'select' && q.options && (
            <div className="flex gap-2 flex-wrap">
              {q.options.map(opt => (
                <button key={opt.value} onClick={() => handleChange(q.id, opt.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${answers[q.id] === opt.value
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  {opt.label || opt.value}
                </button>
              ))}
            </div>
          )}
        </div>
        );
      })}

      {/* Navigation */}
      <div className="flex justify-between">
        <button onClick={onPrev}
          disabled={isFirstPage}
          className={`px-8 py-4 rounded-2xl font-bold flex items-center gap-2 transition-colors ${isFirstPage
            ? 'opacity-0 pointer-events-none'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
          }`}
        >
          <ChevronLeft size={20} /> 返回上一步
        </button>
        <button onClick={handleNext}
          className="px-8 py-4 rounded-2xl font-bold flex items-center gap-2 shadow-xl transition-all bg-slate-900 dark:bg-white text-white dark:text-slate-950 hover:bg-slate-700 dark:hover:bg-blue-50"
        >
          {nextLabel || '下一步'} <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default FlowPage;
